/**
 * MatViewRefreshFormBody — shared form body consumed by both the inline
 * `InlineDdlMatViewRefreshPopover` (Variant 1) and the Designer
 * `ViewEditorRefreshAction` popover. Owns the engine-specific options input
 * (postgres CONCURRENTLY + WITH NO DATA / oracle method dropdown), submit
 * dispatch, error surface, and toast notifications.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D8.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import { toast } from '../../../../../components/ui/toaster';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { QualifiedName, SqlEngine } from '../../../../sql-grammar/model';
import {
  executeRefreshMaterializedView,
  type ExecuteRefreshArgs,
  type OracleRefreshMethod,
  type RefreshMaterializedViewOptions,
} from '../../services/refreshMaterializedView';

export interface MatViewRefreshFormBodyProps {
  readonly engine: SqlEngine;
  readonly connectionId: string | null;
  readonly database: string | null;
  readonly viewName: QualifiedName;
  readonly testId: string;
  readonly onCommitted: () => void;
  readonly onCancel: () => void;
  /** Test seam — overrides `executeRefreshMaterializedView`. */
  readonly executeOverride?: (
    args: ExecuteRefreshArgs,
  ) => ReturnType<typeof executeRefreshMaterializedView>;
}

const NO_CONNECTION_TOOLTIP =
  'Connect the editor tab to a saved connection to refresh the materialized view.';

export function MatViewRefreshFormBody({
  engine,
  connectionId,
  database,
  viewName,
  testId,
  onCommitted,
  onCancel,
  executeOverride,
}: MatViewRefreshFormBodyProps) {
  const [submitting, setSubmitting] = useState(false);
  const [pgsqlConcurrently, setPgsqlConcurrently] = useState(false);
  const [pgsqlWithNoData, setPgsqlWithNoData] = useState(false);
  const [oracleMethod, setOracleMethod] = useState<OracleRefreshMethod>('complete');
  const [error, setError] = useState<string | null>(null);
  const { track } = useAnalytics();

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    if (!connectionId) {
      setError(NO_CONNECTION_TOOLTIP);
      return;
    }
    if (!viewName.name || viewName.name.length === 0) {
      setError('Could not resolve the materialized-view name.');
      return;
    }
    setSubmitting(true);
    const options: RefreshMaterializedViewOptions =
      engine === 'postgres'
        ? { pgsqlConcurrently, pgsqlWithNoData }
        : { oracleMethod };
    const exec = executeOverride ?? executeRefreshMaterializedView;
    try {
      const result = await exec({
        engine,
        connectionId,
        database,
        name: viewName,
        options,
      });
      if (result.success) {
        track('feature:vqe_ddl_visual_edit', {
          op: 'refresh-materialized-view',
          objectKind: 'materialized-view',
          engine,
        });
        toast.success({
          title: 'Materialized view refreshed',
          description: 'The refresh statement executed against the active connection.',
        });
        onCommitted();
      } else {
        setError(result.error ?? 'Refresh failed.');
        toast.error({ title: 'Refresh failed', description: result.error ?? 'Unknown error' });
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      setError(message);
      toast.error({ title: 'Refresh failed', description: message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="text-sm font-semibold">Refresh materialized view</div>
      <div className="text-xs text-muted-foreground" data-testid={`${testId}-name`}>
        {viewName.name ? viewName.name : '(unable to resolve name)'}
      </div>
      {engine === 'postgres' ? (
        <div className="flex flex-col gap-2 text-xs">
          <label className="flex items-center gap-2">
            <Checkbox
              checked={pgsqlConcurrently}
              onCheckedChange={(checked) => setPgsqlConcurrently(checked === true)}
              data-testid={`${testId}-concurrently`}
            />
            <span>CONCURRENTLY (avoid an exclusive lock; matview must have a UNIQUE index)</span>
          </label>
          <label className="flex items-center gap-2">
            <Checkbox
              checked={pgsqlWithNoData}
              onCheckedChange={(checked) => setPgsqlWithNoData(checked === true)}
              data-testid={`${testId}-with-no-data`}
            />
            <span>WITH NO DATA (truncate the matview to empty)</span>
          </label>
        </div>
      ) : (
        <div className="flex flex-col gap-1 text-xs">
          <span className="text-muted-foreground">Refresh method</span>
          <Select
            value={oracleMethod}
            onValueChange={(v) => setOracleMethod(v as OracleRefreshMethod)}
          >
            <SelectTrigger className="h-8 text-xs" data-testid={`${testId}-method`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="complete">COMPLETE — recompute the matview</SelectItem>
              <SelectItem value="fast">FAST — apply log-based deltas</SelectItem>
              <SelectItem value="force">FORCE — fast if possible, else complete</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {error ? (
        <div
          className="rounded-md border border-red-500/40 bg-red-50 px-2 py-1 text-xs text-red-800 dark:bg-red-950 dark:text-red-200"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => {
            void handleSubmit();
          }}
          disabled={submitting || !connectionId}
          data-testid={`${testId}-submit`}
        >
          {submitting ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>
    </div>
  );
}
